import { isAxiosError } from "axios";

interface ApiErrorResponse {
  status: number;
  message: string;
}

/** Extrae texto legible de mensajes NestJS / class-validator (string, array, objeto por campo). */
function flattenMessagePayload(raw: unknown): string[] {
  if (raw == null) return [];
  if (typeof raw === "string" && raw.trim()) return [raw.trim()];
  if (typeof raw === "number" || typeof raw === "boolean") return [String(raw)];
  if (Array.isArray(raw)) {
    const out: string[] = [];
    for (const item of raw) {
      out.push(...flattenMessagePayload(item));
    }
    return out;
  }
  if (typeof raw === "object") {
    const out: string[] = [];
    for (const value of Object.values(raw as Record<string, unknown>)) {
      out.push(...flattenMessagePayload(value));
    }
    return out;
  }
  return [];
}

function normalizeBackendMessage(raw: unknown, fallback: string): string {
  const flat = flattenMessagePayload(raw);
  if (flat.length) return [...new Set(flat)].join("; ");
  return fallback;
}

function fallbackForStatus(status: number): string {
  if (status === 401) return "No autorizado, inicia sesión nuevamente";
  if (status === 404) return "Recurso no encontrado";
  if (status === 422) return "Datos enviados inválidos";
  if (status === 400) return "Solicitud incorrecta";
  if (status === 413) return "El archivo supera el tamaño permitido";
  if (status === 500) return "Error interno del servidor";
  return "Error al comunicarse con el servidor";
}

export function handleApiError(error: unknown, defaultMessage: string): ApiErrorResponse {
  if (isAxiosError(error)) {
    const status = error.response?.status ?? 500;
    const data = error.response?.data as Record<string, unknown> | undefined;
    const rawMessage = data?.message ?? data?.error;
    const parsed = normalizeBackendMessage(rawMessage, "");
    const message =
      parsed ||
      (typeof data?.error === "string" && data.error.trim() && data.error !== "Bad Request"
        ? data.error.trim()
        : fallbackForStatus(status));

    return { status, message: message || defaultMessage };
  }

  return { status: 500, message: defaultMessage };
}

/** Igual que en la tienda: interceptor `{ message }` o Axios / `Error`. */
export function messageFromApiFailure(error: unknown, fallback: string): string {
  if (error && typeof error === "object") {
    const o = error as { message?: unknown; status?: number };
    if (typeof o.message === "string" && o.message.trim()) return o.message.trim();
    if (o.message != null) {
      const fromPayload = normalizeBackendMessage(o.message, "");
      if (fromPayload) return fromPayload;
    }
  }
  if (isAxiosError(error)) {
    return handleApiError(error, fallback).message;
  }
  if (error instanceof Error && error.message.trim()) return error.message.trim();
  return fallback;
}